import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/**
 * Climate UI State
 *
 * Manages ONLY client-side UI state for Organizational Climate module.
 * Server data is handled by TanStack Query hooks.
 *
 * This store contains:
 * - Active tab selection (resultados, historico, insights)
 * - View mode (grid, list)
 * - Expanded cards state
 * - Filter selections (period, department)
 *
 * DO NOT store server data here (climate trends, surveys, etc.)
 */
interface ClimateUIStore {
  // ================================
  // TAB NAVIGATION
  // ================================

  /** Current active tab */
  activeTab: 'resultados' | 'historico' | 'insights'

  /** Set active tab */
  setActiveTab: (tab: ClimateUIStore['activeTab']) => void

  // ================================
  // VIEW MODE
  // ================================

  /** Current view mode */
  viewMode: 'grid' | 'list'

  /** Set view mode */
  setViewMode: (mode: ClimateUIStore['viewMode']) => void

  // ================================
  // EXPANSION STATE
  // ================================

  /** Expanded cards state (for accordion-style UI) */
  expandedCards: Record<string, boolean>

  /** Toggle card expansion */
  toggleCard: (cardId: string) => void

  /** Expand all cards */
  expandAll: () => void

  /** Collapse all cards */
  collapseAll: () => void

  // ================================
  // FILTERS
  // ================================

  /** Selected period in months */
  selectedPeriod: number

  /** Set selected period */
  setSelectedPeriod: (period: number) => void

  /** Selected department ID */
  selectedDepartment: string | undefined

  /** Set selected department */
  setSelectedDepartment: (departmentId: string | undefined) => void

  // ================================
  // UTILITY
  // ================================

  /** Reset all UI state */
  reset: () => void
}

const initialState = {
  activeTab: 'resultados' as const,
  viewMode: 'grid' as const,
  expandedCards: {},
  selectedPeriod: 12,
  selectedDepartment: undefined,
}

/**
 * Climate UI Store
 *
 * Persisted to localStorage so tab/view/filter preferences survive page refreshes.
 * Expansion state is NOT persisted (resets on refresh).
 */
export const useClimateUIStore = create<ClimateUIStore>()(
  persist(
    (set) => ({
      ...initialState,

      // Tab navigation
      setActiveTab: (tab) => set({ activeTab: tab }),

      // View mode
      setViewMode: (mode) => set({ viewMode: mode }),

      // Expansion state
      toggleCard: (cardId) =>
        set((state) => ({
          expandedCards: {
            ...state.expandedCards,
            [cardId]: !state.expandedCards[cardId],
          },
        })),
      expandAll: () =>
        set((state) => {
          const allExpanded = Object.keys(state.expandedCards).reduce(
            (acc, key) => ({ ...acc, [key]: true }),
            {}
          )
          return { expandedCards: allExpanded }
        }),
      collapseAll: () => set({ expandedCards: {} }),

      // Filters
      setSelectedPeriod: (period) => set({ selectedPeriod: period }),
      setSelectedDepartment: (departmentId) => set({ selectedDepartment: departmentId }),

      // Utility
      reset: () => set(initialState),
    }),
    {
      name: 'climate-ui-store', // localStorage key
      // Only persist tab, view, and filters (not expansion state)
      partialize: (state) => ({
        activeTab: state.activeTab,
        viewMode: state.viewMode,
        selectedPeriod: state.selectedPeriod,
        selectedDepartment: state.selectedDepartment,
      }),
    }
  )
)
